import { SearchIcon } from 'lucide-react';

import React, { useLayoutEffect, useRef, useState } from 'react';

import {
  DECIMAL_INPUT_PATTERN,
  isValidDecimalInput,
  normalizeDecimalInput,
  orderPickerMembers,
} from '@splitfiesta/core';
import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import { SplitMethod, formatAmountForCurrency } from '@splitfiesta/shared';
import { Avatar } from '@splitfiesta/ui';

import { SelectCircle } from '@/components/common/SelectCircle';
import { SharesStepper } from '@/components/common/SharesStepper';
import { PlaceholderBadge } from '@/components/members/PlaceholderBadge';
import CurrencyInput from '@/components/ui/CurrencyInput';
import { PercentageSlider } from '@/components/ui/PercentageSlider';

import { resolveUserName } from '@/lib/displayName';
import {
  borderClasses,
  focusRingClasses,
  textColorClasses,
} from '@/lib/styles/common';

import { Split } from './types';

const NO_IDS: readonly string[] = [];

interface SplitMemberListProps {
  splits: Split[];
  splitMethod: SplitMethod;
  currency: string;
  currentUserId?: string;
  /** Spots saved during this form session; they stay last, in that order. */
  newSpotIds?: readonly string[];
  isLoading?: boolean;
  getEqualSplitAmount: (userId: string) => number;
  getSharesSplitAmount: (userId: string) => number;
  getEffectiveShares: (userId: string) => number;
  onIncludeToggle: (userId: string) => void;
  onPercentageChange: (userId: string, value: string) => void;
  onPercentageSliderChange: (userId: string, value: number) => void;
  onAmountChange: (userId: string, value: string | undefined) => void;
  onSharesChange: (userId: string, value: string) => void;
  numericAmount: number;
}

export function SplitMemberList({
  splits,
  splitMethod,
  currency,
  currentUserId,
  newSpotIds = NO_IDS,
  isLoading = false,
  getEqualSplitAmount,
  getSharesSplitAmount,
  getEffectiveShares,
  onIncludeToggle,
  onPercentageChange,
  onPercentageSliderChange,
  onAmountChange,
  onSharesChange,
  numericAmount,
}: SplitMemberListProps) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');

  // The placeholder badge and the pending-claim hint both come from `groups`.
  useNamespace('groups');
  useNamespace('mobile');

  // Track previous included state to detect changes for animation
  const prevIncludedRef = useRef<Map<string, boolean>>(new Map());
  const itemRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Detect when a split's included status changes and trigger animation via DOM
  useLayoutEffect(() => {
    splits.forEach(split => {
      const wasIncluded = prevIncludedRef.current.get(split.userId);
      if (wasIncluded !== undefined && wasIncluded !== split.included) {
        // Item changed - trigger animation via DOM
        const element = itemRefs.current.get(split.userId);
        if (element) {
          element.style.animation = 'none';
          // Force reflow to restart animation
          void element.offsetHeight;
          element.style.animation = 'splitMemberFade 300ms ease-out';
        }
      }
      prevIncludedRef.current.set(split.userId, split.included);
    });
  }, [splits]);

  // Local state for percentage editing (allows free typing before committing)
  const [editingPercentageFor, setEditingPercentageFor] = useState<
    string | null
  >(null);
  const [localPercentageInput, setLocalPercentageInput] = useState<string>('');

  // Handle percentage input change (store raw string for free typing)
  const handleLocalPercentageChange = (value: string) => {
    if (isValidDecimalInput(value)) {
      setLocalPercentageInput(value);
    }
  };

  // Save percentage and exit edit mode
  const savePercentage = (userId: string) => {
    const numValue = normalizeDecimalInput(localPercentageInput);
    const finalValue = !isNaN(numValue) ? String(Math.min(numValue, 100)) : '0';
    onPercentageChange(userId, finalValue);
    setEditingPercentageFor(null);
    setLocalPercentageInput('');
  };

  const includedSplits = splits.filter(s => s.included);

  // Rows stay put when ticked, and a spot saved here lands right above the
  // "Save a spot" row instead of jumping into the list.
  const filteredSplits = orderPickerMembers(
    splits.filter(split =>
      split.userName.toLowerCase().includes(searchTerm.toLowerCase())
    ),
    { currentUserId: currentUserId ?? '', newIds: newSpotIds }
  );

  return (
    <div className="space-y-4">
      {/* Search box */}
      {splits.length > 3 && (
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
          <input
            type="text"
            placeholder={t('expenses:labels.searchMembers')}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className={`w-full pl-10 pr-4 py-2 border ${borderClasses.default} rounded-lg text-sm ${textColorClasses.secondary} dark:bg-gray-800 placeholder-gray-500 dark:placeholder-gray-400 ${focusRingClasses}`}
            disabled={isLoading}
            data-testid="split-member-search"
          />
        </div>
      )}

      {/* Members list - scrollable container */}
      <div className="-mx-6 divide-y divide-gray-100 dark:divide-gray-800 max-h-[216px] overflow-y-auto">
        {filteredSplits.map(split => {
          const equalAmount = getEqualSplitAmount(split.userId);
          // Show second row for percentage, custom, and shares
          const showInputs = split.included && splitMethod !== 'equal';

          return (
            <div
              key={split.userId}
              ref={el => {
                if (el) {
                  itemRefs.current.set(split.userId, el);
                } else {
                  itemRefs.current.delete(split.userId);
                }
              }}
              onClick={() =>
                !isLoading && !showInputs && onIncludeToggle(split.userId)
              }
              className={`px-6 py-1 transition-all duration-200 ease-in-out ${
                showInputs
                  ? ''
                  : 'cursor-pointer select-none hover:bg-gray-50 dark:hover:bg-gray-800/50'
              } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''} ${
                !split.included ? 'opacity-60' : ''
              }`}
              data-testid={`split-member-${split.userId}`}
            >
              <div className="flex flex-col gap-0.5">
                {/* First row: checkbox, avatar, name, and amount (for equal split) */}
                <div className="relative flex items-center gap-2">
                  <input
                    type="checkbox"
                    id={`split-${split.userId}`}
                    checked={split.included}
                    onChange={() => onIncludeToggle(split.userId)}
                    disabled={isLoading}
                    className="peer sr-only"
                    onClick={e => e.stopPropagation()}
                    data-testid={`member-include-checkbox-${split.userId}`}
                  />
                  <label
                    htmlFor={`split-${split.userId}`}
                    className={`flex shrink-0 rounded-full ${isLoading ? 'cursor-not-allowed' : 'cursor-pointer'} peer-focus-visible:ring-2 peer-focus-visible:ring-primary-500 peer-focus-visible:ring-offset-2 dark:peer-focus-visible:ring-offset-gray-900`}
                    onClick={e => e.stopPropagation()}
                  >
                    <SelectCircle
                      checked={split.included}
                      disabled={isLoading}
                    />
                  </label>
                  <label
                    htmlFor={`split-${split.userId}`}
                    className={`flex items-center gap-2 min-w-0 ${isLoading ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                    onClick={e => e.stopPropagation()}
                  >
                    <Avatar
                      name={split.userName}
                      src={split.profileImage}
                      size="xs"
                      className="shrink-0"
                    />
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center min-w-0">
                      <span className="truncate">
                        {resolveUserName(split.userName, t)}
                      </span>
                      {split.userId === currentUserId && (
                        <span className="text-gray-500 dark:text-gray-400 font-normal ml-1 shrink-0">
                          {t('common:labels.you')}
                        </span>
                      )}
                      {split.isPlaceholder && (
                        <PlaceholderBadge className="ml-1 shrink-0" />
                      )}
                      {split.claimPendingFor && (
                        <span className="ml-1 shrink-0 text-xs font-normal text-gray-500 dark:text-gray-400">
                          {t('groups:members.claimPendingFor', {
                            claimant: split.claimPendingFor,
                          })}
                        </span>
                      )}
                    </div>
                  </label>

                  {/* Dashed line connects name to amount/input */}
                  {/* Non-custom: show on both mobile and desktop */}
                  {split.included && splitMethod !== 'custom' && (
                    <div className="flex-1 flex items-center mx-2">
                      <div className="flex-1 border-b border-dashed border-gray-300 dark:border-gray-600" />
                      <span className="text-gray-300 dark:text-gray-600 text-xs ml-1">
                        &gt;
                      </span>
                    </div>
                  )}
                  {/* Custom: show only on desktop (mobile has second row) */}
                  {split.included && splitMethod === 'custom' && (
                    <div className="hidden sm:flex flex-1 items-center mx-2">
                      <div className="flex-1 border-b border-dashed border-gray-300 dark:border-gray-600" />
                      <span className="text-gray-300 dark:text-gray-600 text-xs ml-1">
                        &gt;
                      </span>
                    </div>
                  )}

                  {/* Equal split amount - inline */}
                  {split.included &&
                    splitMethod === 'equal' &&
                    includedSplits.length > 0 && (
                      <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap shrink-0">
                        {formatAmountForCurrency(equalAmount, currency)}
                      </div>
                    )}

                  {/* Percentage amount on name row (prevents jumping when slider changes) */}
                  {split.included && splitMethod === 'percentage' && (
                    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap shrink-0">
                      {formatAmountForCurrency(
                        ((split.percentage || 0) / 100) * numericAmount,
                        currency
                      )}
                    </div>
                  )}

                  {/* Shares amount on name row (prevents jumping when stepper changes) */}
                  {split.included && splitMethod === 'shares' && (
                    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap shrink-0">
                      {formatAmountForCurrency(
                        getSharesSplitAmount(split.userId),
                        currency
                      )}
                    </div>
                  )}

                  {/* Desktop only: Custom amount input inline */}
                  {split.included && splitMethod === 'custom' && (
                    <div
                      className="hidden sm:flex items-center shrink-0"
                      onClick={e => e.stopPropagation()}
                    >
                      <CurrencyInput
                        value={split.amountFormatted || ''}
                        onValueChange={value =>
                          onAmountChange(split.userId, value)
                        }
                        currency={currency}
                        className="!w-[120px] !text-sm !py-1 !rounded !text-right"
                        disabled={isLoading}
                        data-testid={`member-split-input-${split.userId}`}
                      />
                    </div>
                  )}
                </div>

                {/* Second row for percentage/shares controls (indented under avatar) */}
                {split.included &&
                  (splitMethod === 'shares' ||
                    splitMethod === 'percentage') && (
                    <div className="ml-[72px] flex items-center justify-end gap-2">
                      {/* Percentage: slider + input + amount */}
                      {splitMethod === 'percentage' && (
                        <div
                          className="flex items-center gap-2 flex-1"
                          onClick={e => e.stopPropagation()}
                        >
                          <PercentageSlider
                            value={split.percentage || 0}
                            onChange={value =>
                              onPercentageSliderChange(split.userId, value)
                            }
                            disabled={isLoading}
                            data-testid={`member-percentage-slider-${split.userId}`}
                          />
                          {editingPercentageFor === split.userId ? (
                            <input
                              type="text"
                              inputMode="decimal"
                              pattern={DECIMAL_INPUT_PATTERN}
                              value={localPercentageInput}
                              onChange={e =>
                                handleLocalPercentageChange(e.target.value)
                              }
                              onBlur={() => savePercentage(split.userId)}
                              onKeyDown={e => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  savePercentage(split.userId);
                                } else if (e.key === 'Escape') {
                                  setEditingPercentageFor(null);
                                  setLocalPercentageInput('');
                                }
                              }}
                              autoFocus
                              autoComplete="off"
                              placeholder="0"
                              data-testid={`member-percentage-input-${split.userId}`}
                              disabled={isLoading}
                              className="w-12 pl-1 pr-1 py-0.5 text-sm text-center border border-orange-400 dark:border-orange-500 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 disabled:opacity-50 focus:ring-2 focus:ring-orange-500"
                            />
                          ) : (
                            <button
                              type="button"
                              onClick={() => {
                                setLocalPercentageInput(
                                  String(split.percentage || '')
                                );
                                setEditingPercentageFor(split.userId);
                              }}
                              disabled={isLoading}
                              data-testid={`member-percentage-input-${split.userId}`}
                              className="w-12 pl-1 pr-1 py-0.5 text-sm text-center border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 hover:border-gray-400 dark:hover:border-gray-500 transition-colors"
                            >
                              {split.percentage || 0}
                            </button>
                          )}
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            %
                          </span>
                        </div>
                      )}

                      {/* Shares stepper */}
                      {splitMethod === 'shares' && (
                        <div
                          className="flex items-center gap-1"
                          onClick={e => e.stopPropagation()}
                        >
                          <SharesStepper
                            value={getEffectiveShares(split.userId)}
                            onChange={next =>
                              onSharesChange(split.userId, String(next))
                            }
                            disabled={isLoading}
                            testIdPrefix="member-shares"
                            testIdSuffix={split.userId}
                          />
                        </div>
                      )}
                    </div>
                  )}

                {/* Mobile only: Second row for custom amount input */}
                {split.included && splitMethod === 'custom' && (
                  <div
                    className="sm:hidden ml-[72px] flex items-center justify-end"
                    onClick={e => e.stopPropagation()}
                  >
                    <CurrencyInput
                      value={split.amountFormatted || ''}
                      onValueChange={value =>
                        onAmountChange(split.userId, value)
                      }
                      currency={currency}
                      className="!w-[120px] !text-sm !py-1 !rounded !text-right"
                      disabled={isLoading}
                      data-testid={`member-split-input-${split.userId}`}
                    />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
